import { useState, useEffect, useCallback } from "react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { Notification, NotificationCountResponse } from "@/types/notification";

interface UseNotificationsOptions {
  loadNotificationsOnMount?: boolean;
  loadUnreadCountOnMount?: boolean;
}

let notificationsCache: Notification[] | null = null;
let notificationsCacheAt = 0;
let unreadCountCache: number | null = null;
let unreadCountCacheAt = 0;

const NOTIFICATIONS_CACHE_TTL_MS = 10000;
const UNREAD_COUNT_CACHE_TTL_MS = 5000;

const hasFreshNotificationsCache = () =>
  notificationsCache !== null &&
  Date.now() - notificationsCacheAt < NOTIFICATIONS_CACHE_TTL_MS;

const hasFreshUnreadCountCache = () =>
  unreadCountCache !== null &&
  Date.now() - unreadCountCacheAt < UNREAD_COUNT_CACHE_TTL_MS;

export const useNotifications = ({
  loadNotificationsOnMount = true,
  loadUnreadCountOnMount = true,
}: UseNotificationsOptions = {}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications and keep the full list in state.
  const fetchNotifications = useCallback(async () => {
    if (hasFreshNotificationsCache()) {
      setNotifications(notificationsCache!);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
  
      const response = await axiosInstance.get<Notification[]>(
        endpoints.notifications
      );

      notificationsCache = response.data;
      notificationsCacheAt = Date.now();
      setNotifications(response.data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);
  

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (hasFreshUnreadCountCache()) {
      setUnreadCount(unreadCountCache ?? 0);
      return;
    }

    try {
      const response = await axiosInstance.get<NotificationCountResponse>(
        endpoints.notificationsUnreadCount
      );
      unreadCountCache = response.data.count;
      unreadCountCacheAt = Date.now();
      setUnreadCount(response.data.count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await axiosInstance.post(
        endpoints.notificationMarkRead(notificationId)
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
      notificationsCache = notificationsCache?.map((n) =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ) ?? null;
      unreadCountCache = unreadCountCache !== null ? Math.max(0, unreadCountCache - 1) : null;
      unreadCountCacheAt = Date.now();
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await axiosInstance.post(endpoints.notificationsMarkAllRead);

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );

      setUnreadCount(0);
      notificationsCache = notificationsCache?.map((n) => ({ ...n, is_read: true })) ?? null;
      unreadCountCache = 0;
      unreadCountCacheAt = Date.now();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  }, []);

  // Delete single notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        await axiosInstance.delete(
          endpoints.notificationDetail(notificationId)
        );

        const deleted = notifications.find((n) => n.id === notificationId);

        setNotifications((prev) =>
          prev.filter((n) => n.id !== notificationId)
        );

        if (deleted && !deleted.is_read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        notificationsCache = notificationsCache?.filter((n) => n.id !== notificationId) ?? null;
        if (deleted && !deleted.is_read && unreadCountCache !== null) {
          unreadCountCache = Math.max(0, unreadCountCache - 1);
          unreadCountCacheAt = Date.now();
        }
      } catch (err) {
        console.error("Failed to delete notification:", err);
      }
    },
    [notifications]
  );

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    try {
      await axiosInstance.delete(endpoints.notificationsClearAll);
      setNotifications([]);
      setUnreadCount(0);
      notificationsCache = [];
      notificationsCacheAt = Date.now();
      unreadCountCache = 0;
      unreadCountCacheAt = Date.now();
    } catch (err) {
      console.error("Failed to clear notifications:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (loadNotificationsOnMount) {
      fetchNotifications();
    } else {
      setLoading(false);
    }

    if (loadUnreadCountOnMount) {
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount, loadNotificationsOnMount, loadUnreadCountOnMount]);

  // Poll unread count every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetch: fetchNotifications,
  };
};
